'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';
import { useSearchParams } from 'next/navigation';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';

import { CuteSpinner } from '@/components/common/CuteSpinner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { env } from '@/env';
import { createAuthClientAdapter } from '@/lib/auth/auth-client-adapter';
import { authClient } from '@/lib/auth-client';
import { useDeleteSocialCookies, useUpdateSocialCookies } from '@/lib/hooks/api/social/cookies';
import { notifyOfflineModeChanged } from '@/lib/offline-mode';
import { SWEETLINK_AUTO_KEY, SWEETLINK_AUTO_RESUME_FROM_KEY } from '@/lib/sweetlink/constants';
import { setSweetLinkStatusSnapshot } from '@/lib/sweetlink/status-store';
import { trpc } from '@/lib/trpc/client';
import { createTrpcSocialAdapter } from '@/lib/trpc/social-adapter';
import { getBrowserWindow } from '@/lib/utils/browser-window';
import { getRecordValue, isRecord, toTrimmedNonEmptyString } from '@/lib/utils/type-guards';
import { getDesktopStatusLabel } from '../desktopStatusLabels';
import { useDesktopTwitterLogin } from '../useDesktopTwitterLogin';

type DataSourceOption = 'twitter' | 'graphql' | 'hybrid';

const BUILD_MODE = toTrimmedNonEmptyString(env.NEXT_PUBLIC_BUILD_MODE) ?? 'web';
const APP_URL = toTrimmedNonEmptyString(env.NEXT_PUBLIC_APP_URL) ?? 'http://localhost:3000';
const LOWER_APP_URL = APP_URL.toLowerCase();
const DEV_SIGN_IN_PATH = 'dev/sign-in';
const TWITTER_SIGN_IN_PATH = '/api/auth/sign-in/social';
const SWEETLINK_AUTO_STORAGE_KEY = SWEETLINK_AUTO_KEY;

const getSafeAuthClient = () => createAuthClientAdapter(authClient);

interface DevelopmentLoginResponseShape {
  data?: unknown;
  error?: unknown;
}

const extractDevelopmentLoginErrorMessage = (errorValue: unknown): string | null => {
  const parsed = DevLoginErrorSchema.safeParse(errorValue);
  if (!parsed.success) {
    return null;
  }
  const status = parsed.data.status ? parsed.data.status.toLocaleString() : 'unknown';
  const statusText = toTrimmedNonEmptyString(parsed.data.statusText) ?? 'Request Failed';
  const message = toTrimmedNonEmptyString(parsed.data.message);
  return `Dev login failed (${status} ${statusText})${message ? `: ${message}` : ''}`;
};

const hasDevelopmentLoginSession = (dataValue: unknown): boolean => {
  const parsed = HasSessionSchema.safeParse(dataValue);
  if (!parsed.success) {
    return false;
  }
  const sessionToken = toTrimmedNonEmptyString(parsed.data.sessionToken);
  if (sessionToken) {
    return true;
  }
  return Boolean(parsed.data.user);
};

interface NormalizedCookieStatus {
  hasCookies: boolean;
  lastUpdated: Date | null;
}

const normalizeCookieStatus = (value: unknown): NormalizedCookieStatus | null => {
  const parsed = CookieStatusSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }
  const hasCookies = Boolean(parsed.data.hasCookies);
  const lastUpdatedRaw = parsed.data.lastUpdated;
  if (lastUpdatedRaw instanceof Date) {
    return { hasCookies, lastUpdated: lastUpdatedRaw };
  }
  if (typeof lastUpdatedRaw === 'string') {
    const parsedDate = new Date(lastUpdatedRaw);
    if (!Number.isNaN(parsedDate.getTime())) {
      return { hasCookies, lastUpdated: parsedDate };
    }
  }
  return { hasCookies, lastUpdated: null };
};

async function attemptDevelopmentLogin(): Promise<boolean> {
  console.info('[auth] dev login requesting', DEV_SIGN_IN_PATH);
  const safeAuthClient = getSafeAuthClient();
  const response = await safeAuthClient.$fetch<DevelopmentLoginResponseShape>(DEV_SIGN_IN_PATH, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'content-type': 'application/json',
    },
    body: '{}',
  });
  console.info('[auth] dev login response', response);

  const parsedResponse = DevLoginResponseSchema.safeParse(response);
  if (!parsedResponse.success) {
    throw new Error('Dev login failed (invalid response payload)');
  }

  const errorMessage = extractDevelopmentLoginErrorMessage(parsedResponse.data.error);
  if (errorMessage) {
    throw new Error(errorMessage);
  }

  return hasDevelopmentLoginSession(parsedResponse.data.data);
}

interface TwitterSignInOptions {
  callbackURL?: string;
  errorCallbackURL?: string;
  newUserCallbackURL?: string;
}

const resolveCallbackUrl = (candidate: string | undefined): string | undefined => {
  if (!candidate) {
    return;
  }
  if (candidate.startsWith('http://') || candidate.startsWith('https://')) {
    return candidate;
  }
  const runtimeWindow = getBrowserWindow();
  if (!runtimeWindow?.location) {
    return candidate;
  }
  try {
    return new URL(candidate, runtimeWindow.location.origin).toString();
  } catch {
    return candidate;
  }
};

const initiateTwitterOAuth = async (options: TwitterSignInOptions = {}): Promise<void> => {
  const resolvedCallbackURL = resolveCallbackUrl(options.callbackURL);
  const resolvedNewUserCallbackURL = resolveCallbackUrl(options.newUserCallbackURL);
  const resolvedErrorCallbackURL = resolveCallbackUrl(options.errorCallbackURL);
  const response = await fetch(TWITTER_SIGN_IN_PATH, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify({
      provider: 'twitter',
      callbackURL: resolvedCallbackURL,
      newUserCallbackURL: resolvedNewUserCallbackURL,
      errorCallbackURL: resolvedErrorCallbackURL,
    }),
  });

  if (!response.ok) {
    throw new Error(`Twitter sign-in request failed (${response.status} ${response.statusText})`);
  }

  const payload: unknown = await response.json();
  if (isRecord(payload)) {
    const redirect = getRecordValue(payload, 'redirect');
    const url = getRecordValue(payload, 'url');
    if (redirect === true && typeof url === 'string' && url.length > 0) {
      globalThis.location.assign(url);
    }
  }
};

interface SignInClientProperties {
  dataSource: DataSourceOption;
}

export function SignInClient({ dataSource }: SignInClientProperties) {
  const isDesktopBuild = BUILD_MODE === 'desktop';
  return isDesktopBuild ? <DesktopSignInContent /> : <WebSignInContent dataSource={dataSource} />;
}

function DesktopSignInContent() {
  const { status, error } = useDesktopTwitterLogin();
  const statusLabel = getDesktopStatusLabel(status);

  return (
    <div className="flex flex-col items-center justify-center gap-4 text-center" aria-live="polite">
      <CuteSpinner size={32} className="text-primary border-primary/30 border-t-primary" />
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Connect Sweetistics Desktop to Twitter</p>
        <p className="text-sm text-muted-foreground">
          A secure Twitter window has opened. Complete the login and we&apos;ll capture your cookies locally.
        </p>
        <p className="text-xs text-muted-foreground">{statusLabel}</p>
        {error ? <p className="text-xs text-destructive">Error: {error}</p> : null}
      </div>
    </div>
  );
}

function WebSignInContent({ dataSource }: { dataSource: DataSourceOption }) {
  if (dataSource === 'graphql') {
    return <GraphqlSignInContent mode="graphql" />;
  }
  if (dataSource === 'hybrid') {
    return <GraphqlSignInContent mode="hybrid" />;
  }
  return <DefaultWebSignInContent />;
}

type SignInWindow = Window & {
  __sweetisticsTwitterLoginInFlight__?: boolean;
  __sweetisticsSignInPageLoadId__?: string;
  __sweetlinkCliAuto?: boolean;
  __sweetlinkCliAutoInFlight?: boolean;
};
const LOGIN_IN_FLIGHT_STORAGE_KEY = 'sweetistics:twitter-login-in-flight';
const LOGIN_IN_FLIGHT_TTL_MS = 60_000;

const ensurePageLoadId = (runtimeWindow: SignInWindow): string => {
  if (runtimeWindow.__sweetisticsSignInPageLoadId__) {
    return runtimeWindow.__sweetisticsSignInPageLoadId__;
  }
  const runtimeCrypto = runtimeWindow.crypto ?? globalThis.crypto;
  const generatedId =
    typeof runtimeCrypto?.randomUUID === 'function'
      ? runtimeCrypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  runtimeWindow.__sweetisticsSignInPageLoadId__ = generatedId;
  return generatedId;
};

interface LoginInFlightMetadata {
  pageLoadId: string | null;
  timestamp: number;
}

const TimestampSchema = z.preprocess((value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return;
    }
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return;
}, z.number());

const LoginInFlightMetadataSchema = z
  .object({
    timestamp: TimestampSchema,
    pageLoadId: z.string().trim().min(1).optional().or(z.literal(null)).optional(),
  })
  .passthrough();

const parseLoginInFlightMetadata = (rawValue: string | null): LoginInFlightMetadata | null => {
  if (!rawValue) {
    return null;
  }
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return null;
  }
  try {
    const parsedValue = JSON.parse(trimmed) as unknown;
    const normalized = LoginInFlightMetadataSchema.safeParse(parsedValue);
    if (!normalized.success) {
      return null;
    }
    return {
      timestamp: normalized.data.timestamp,
      pageLoadId: normalized.data.pageLoadId ?? null,
    };
  } catch {
    return null;
  }
};

function DefaultWebSignInContent() {
  const searchParameters = useSearchParams();
  const preferDevelopmentLogin = searchParameters.has('dev') || searchParameters.get('auth') === 'dev';
  const forceTwitterLogin = searchParameters.get('auth') === 'twitter';
  const sweetLinkAutoRequested = searchParameters.get('sweetlink') === 'auto';
  const lowerAppUrl = LOWER_APP_URL;
  const isLocalApp = lowerAppUrl.startsWith('http://localhost') || lowerAppUrl.startsWith('http://127.0.0.1');
  const redirectTarget = useMemo(() => {
    const runtimeWindow = getBrowserWindow();
    const origin = runtimeWindow?.location.origin ?? 'http://localhost:3000';

    const candidate = searchParameters.get('redirect');
    let resolved = '/timeline';

    if (candidate) {
      if (candidate.startsWith('/')) {
        resolved = candidate;
      } else if (runtimeWindow) {
        try {
          const url = new URL(candidate, runtimeWindow.location.origin);
          if (url.origin === runtimeWindow.location.origin) {
            resolved = `${url.pathname}${url.search}${url.hash}`;
          }
        } catch (unknownError) {
          console.warn('Ignoring invalid redirect candidate', unknownError);
        }
      }
    }

    if (!sweetLinkAutoRequested) {
      return resolved;
    }

    try {
      const url = new URL(resolved, origin);
      url.searchParams.set('sweetlink', 'auto');
      return `${url.pathname}${url.search}${url.hash}`;
    } catch {
      return resolved;
    }
  }, [searchParameters, sweetLinkAutoRequested]);

  const shouldAttemptDevelopmentLogin = useMemo(
    () => preferDevelopmentLogin && !forceTwitterLogin && isLocalApp,
    [forceTwitterLogin, isLocalApp, preferDevelopmentLogin]
  );

  const developmentLoginQuery = useQuery({
    queryKey: ['auth', 'dev-login'],
    enabled: shouldAttemptDevelopmentLogin,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
    queryFn: attemptDevelopmentLogin,
  });

  const redirectInitiatedReference = useRef(false);
  const sweetLinkAutoBootstrapTriggeredReference = useRef(false);
  const [sweetLinkBootstrapReady, setSweetLinkBootstrapReady] = useState(false);

  useEffect(() => {
    const runtimeWindow = getBrowserWindow();
    if (!runtimeWindow) {
      return;
    }
    const maybeRedirect = () => {
      try {
        const resumeTarget = runtimeWindow.sessionStorage?.getItem(SWEETLINK_AUTO_RESUME_FROM_KEY);
        if (resumeTarget && resumeTarget !== runtimeWindow.location.href) {
          runtimeWindow.location.replace(resumeTarget);
          return true;
        }
      } catch {
        /* ignore sessionStorage failures */
      }
      return false;
    };
    if (maybeRedirect()) {
      return;
    }
    const intervalId = runtimeWindow.setInterval(() => {
      if (maybeRedirect()) {
        runtimeWindow.clearInterval(intervalId);
      }
    }, 250);
    return () => {
      runtimeWindow.clearInterval(intervalId);
    };
  }, []);

  const stageSweetLinkAutoResume = useCallback((runtimeWindow: SignInWindow, resumeTarget?: string) => {
    if (!sweetLinkAutoBootstrapTriggeredReference.current) {
      return;
    }
    runtimeWindow.__sweetlinkCliAuto = true;
    try {
      runtimeWindow.sessionStorage?.setItem(SWEETLINK_AUTO_STORAGE_KEY, 'resume');
      const runtimeLocation = runtimeWindow.location ?? null;
      let derivedTarget: string | null = null;
      if (runtimeLocation?.href) {
        try {
          derivedTarget = new URL(runtimeLocation.href).toString();
        } catch {
          derivedTarget = runtimeLocation.href;
        }
      }
      const target = resumeTarget ?? derivedTarget ?? undefined;
      if (target) {
        runtimeWindow.sessionStorage?.setItem(SWEETLINK_AUTO_RESUME_FROM_KEY, target);
      }
    } catch {
      /* ignore sessionStorage failures */
    }
  }, []);

  useEffect(() => {
    const runtimeWindow = getBrowserWindow() as SignInWindow | null;
    if (!runtimeWindow) {
      setSweetLinkBootstrapReady(true);
      return;
    }

    let hasSessionStorageAutoFlag = false;
    try {
      const storedValue = runtimeWindow.sessionStorage?.getItem(SWEETLINK_AUTO_STORAGE_KEY);
      hasSessionStorageAutoFlag = storedValue === 'pending' || storedValue === 'resume';
    } catch {
      hasSessionStorageAutoFlag = false;
    }
    const shouldBootstrapSweetLink = sweetLinkAutoRequested || hasSessionStorageAutoFlag;
    if (!shouldBootstrapSweetLink) {
      setSweetLinkBootstrapReady(true);
      return;
    }
    if (sweetLinkAutoBootstrapTriggeredReference.current) {
      return;
    }
    sweetLinkAutoBootstrapTriggeredReference.current = true;
    setSweetLinkBootstrapReady(false);
    runtimeWindow.__sweetlinkCliAuto = true;
    runtimeWindow.__sweetlinkCliAutoInFlight = true;
    try {
      runtimeWindow.sessionStorage?.setItem(SWEETLINK_AUTO_STORAGE_KEY, 'pending');
    } catch {
      /* ignore sessionStorage access issues */
    }
    setSweetLinkStatusSnapshot({ status: 'connecting', reason: null, codename: null });

    let cancelled = false;
    const bootstrapSweetLinkSession = async () => {
      try {
        const sweetlinkModule = await import('@/client/sweetlink/activate');
        await sweetlinkModule.ensureSweetLinkSession({
          copyToClipboard: false,
          pageUrl: runtimeWindow.location.href,
        });
        const currentUrl = new URL(runtimeWindow.location.href);
        if (currentUrl.searchParams.has('sweetlink')) {
          currentUrl.searchParams.delete('sweetlink');
          runtimeWindow.history.replaceState(runtimeWindow.history.state, '', currentUrl.toString());
        }
      } catch (error) {
        console.error('SweetLink CLI bootstrap failed on sign-in route', error);
      } finally {
        runtimeWindow.__sweetlinkCliAutoInFlight = false;
        try {
          runtimeWindow.sessionStorage?.removeItem(SWEETLINK_AUTO_STORAGE_KEY);
        } catch {
          /* ignore sessionStorage access issues */
        }
        if (!cancelled) {
          setSweetLinkBootstrapReady(true);
        }
      }
    };

    bootstrapSweetLinkSession().catch((unknownError: unknown) => {
      console.error('SweetLink CLI bootstrap invocation failed', unknownError);
      runtimeWindow.__sweetlinkCliAutoInFlight = false;
      try {
        runtimeWindow.sessionStorage?.removeItem(SWEETLINK_AUTO_STORAGE_KEY);
      } catch {
        /* ignore */
      }
      if (!cancelled) {
        setSweetLinkBootstrapReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [sweetLinkAutoRequested]);

  useEffect(() => {
    const runtimeWindow = getBrowserWindow() as SignInWindow | null;
    if (!runtimeWindow) {
      return;
    }
    if (!sweetLinkBootstrapReady) {
      return;
    }
    const resolvedRedirectUrl = (() => {
      try {
        return new URL(redirectTarget, runtimeWindow.location.origin ?? 'http://localhost:3000').toString();
      } catch {
        return `${runtimeWindow.location.origin ?? 'http://localhost:3000'}${redirectTarget}`;
      }
    })();
    const pageLoadId = ensurePageLoadId(runtimeWindow);

    const loginInFlightTimestampRaw = runtimeWindow.sessionStorage?.getItem(LOGIN_IN_FLIGHT_STORAGE_KEY);
    const loginInFlightMetadata = parseLoginInFlightMetadata(loginInFlightTimestampRaw);
    const loginInFlightTimestamp = loginInFlightMetadata?.timestamp ?? null;
    const loginInFlightPageId = loginInFlightMetadata?.pageLoadId ?? null;

    const markLoginInFlight = () => {
      redirectInitiatedReference.current = true;
      runtimeWindow.__sweetisticsTwitterLoginInFlight__ = true;
      try {
        runtimeWindow.sessionStorage?.setItem(
          LOGIN_IN_FLIGHT_STORAGE_KEY,
          JSON.stringify({ timestamp: Date.now(), pageLoadId })
        );
      } catch {
        /* ignore sessionStorage failures */
      }
    };

    const clearLoginInFlight = () => {
      redirectInitiatedReference.current = false;
      runtimeWindow.__sweetisticsTwitterLoginInFlight__ = false;
      try {
        runtimeWindow.sessionStorage?.removeItem(LOGIN_IN_FLIGHT_STORAGE_KEY);
      } catch {
        /* ignore sessionStorage failures */
      }
    };

    if (runtimeWindow.__sweetisticsTwitterLoginInFlight__) {
      redirectInitiatedReference.current = true;
      return;
    }

    if (
      loginInFlightTimestamp !== null &&
      Number.isFinite(loginInFlightTimestamp) &&
      loginInFlightPageId === pageLoadId &&
      Date.now() - loginInFlightTimestamp < LOGIN_IN_FLIGHT_TTL_MS
    ) {
      redirectInitiatedReference.current = true;
      return;
    }

    if (
      loginInFlightMetadata &&
      (loginInFlightMetadata.pageLoadId === null || loginInFlightMetadata.pageLoadId !== pageLoadId)
    ) {
      clearLoginInFlight();
    }

    if (redirectInitiatedReference.current) {
      return;
    }

    const startTwitterLogin = async () => {
      markLoginInFlight();
      try {
        stageSweetLinkAutoResume(runtimeWindow, resolvedRedirectUrl);
        await initiateTwitterOAuth({ callbackURL: redirectTarget });
      } catch (unknownError: unknown) {
        console.error('Twitter sign-in failed', unknownError);
        clearLoginInFlight();
      }
    };

    if (shouldAttemptDevelopmentLogin) {
      if (developmentLoginQuery.isSuccess && developmentLoginQuery.data) {
        markLoginInFlight();
        try {
          stageSweetLinkAutoResume(runtimeWindow, resolvedRedirectUrl);
          runtimeWindow.localStorage.setItem('sweetistics.offlineMode', '1');
          runtimeWindow.sessionStorage.setItem('sweetistics.offlineMode', '1');
          notifyOfflineModeChanged();
        } catch (unknownError: unknown) {
          console.warn('Failed to persist offline mode flag', unknownError);
        }
        const redirectUrl = new URL(
          redirectTarget,
          runtimeWindow.location.origin ?? 'http://localhost:3000'
        ).toString();
        runtimeWindow.location.assign(redirectUrl);
        return;
      }

      if (developmentLoginQuery.isError) {
        clearLoginInFlight();
        console.error('Dev login request failed', developmentLoginQuery.error);
        startTwitterLogin().catch((unknownError: unknown) => {
          console.error('Twitter sign-in invocation failed', unknownError);
        });
      }
      return;
    }

    startTwitterLogin().catch((unknownError: unknown) => {
      console.error('Twitter sign-in invocation failed', unknownError);
    });

    return () => {
      if (!redirectInitiatedReference.current) {
        runtimeWindow.__sweetisticsTwitterLoginInFlight__ = false;
      }
    };
  }, [
    developmentLoginQuery.data,
    developmentLoginQuery.error,
    developmentLoginQuery.isError,
    developmentLoginQuery.isSuccess,
    redirectTarget,
    shouldAttemptDevelopmentLogin,
    sweetLinkBootstrapReady,
    stageSweetLinkAutoResume,
  ]);

  const statusMessage = shouldAttemptDevelopmentLogin
    ? 'Checking developer login state...'
    : 'Redirecting to Twitter sign-in...';

  return (
    <div className="flex flex-col items-center justify-center gap-4 text-center" aria-live="polite">
      <CuteSpinner size={32} className="text-primary border-primary/30 border-t-primary" />
      <p className="text-sm text-muted-foreground">{statusMessage}</p>
    </div>
  );
}

function GraphqlSignInContent({ mode }: { mode: 'graphql' | 'hybrid' }) {
  const [oauthPending, setOauthPending] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  const trpcClient = createTrpcSocialAdapter(trpc);
  const searchParameters = useSearchParams();
  const preferDevelopmentLogin = searchParameters.has('dev') || searchParameters.get('auth') === 'dev';
  const lowerAppUrl = LOWER_APP_URL;
  const isLocalApp = lowerAppUrl.startsWith('http://localhost') || lowerAppUrl.startsWith('http://127.0.0.1');
  const shouldAttemptDevelopmentLogin = preferDevelopmentLogin && isLocalApp;

  const cookieStatusQuery = trpcClient.getCookiesStatusQuery(undefined, {
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchInterval: 60_000,
    retry: false,
  });

  const developmentLoginQuery = useQuery({
    queryKey: ['auth', 'dev-login'],
    enabled: shouldAttemptDevelopmentLogin,
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: false,
    queryFn: attemptDevelopmentLogin,
  });

  useEffect(() => {
    if (!shouldAttemptDevelopmentLogin) {
      return;
    }
    if (developmentLoginQuery.isSuccess) {
      const runtimeWindow = getBrowserWindow();
      try {
        runtimeWindow?.localStorage.setItem('sweetistics.offlineMode', '1');
        runtimeWindow?.sessionStorage.setItem('sweetistics.offlineMode', '1');
        notifyOfflineModeChanged();
      } catch (unknownError: unknown) {
        console.warn('Failed to persist offline mode flag', unknownError);
      }
      runtimeWindow?.location.assign('/timeline');
    }
  }, [developmentLoginQuery.isSuccess, shouldAttemptDevelopmentLogin]);

  const oauthLogin = useCallback(async () => {
    setOauthPending(true);
    setOauthError(null);
    try {
      await initiateTwitterOAuth({ callbackURL: '/timeline' });
    } catch (unknownError: unknown) {
      const message = unknownError instanceof Error ? unknownError.message : 'Twitter sign-in failed';
      setOauthError(message);
      setOauthPending(false);
    }
  }, []);

  const handleOauthLogin = useCallback(() => {
    oauthLogin().catch((unknownError: unknown) => {
      console.error('OAuth login invocation failed', unknownError);
    });
  }, [oauthLogin]);

  const { Component: CookieManagerCard } = useCookieManager();

  const cookieStatus = normalizeCookieStatus(cookieStatusQuery.data);
  const isHybrid = mode === 'hybrid';

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Sign in with Twitter</CardTitle>
          <CardDescription>
            {isHybrid
              ? 'Use Twitter OAuth to authenticate, then choose whether to hydrate timelines via GraphQL or classic API.'
              : 'Authenticate with Twitter. We use OAuth for your Sweetistics session and GraphQL cookies for timeline data.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={handleOauthLogin} disabled={oauthPending} className="w-full">
            {oauthPending ? (
              <>
                <CuteSpinner size={16} className="mr-2 border-current/40 border-t-current" />
                Redirecting to Twitter…
              </>
            ) : (
              'Continue with Twitter'
            )}
          </Button>
          {oauthError ? <p className="text-sm text-destructive">Twitter sign-in failed: {oauthError}</p> : null}
          {shouldAttemptDevelopmentLogin && !developmentLoginQuery.isSuccess ? (
            <p className="text-xs text-muted-foreground">
              Dev login requested – attempting to sign you in locally. You can still use the cookie path below.
            </p>
          ) : null}
          {cookieStatus ? (
            <p className="text-xs text-muted-foreground">
              {cookieStatus.hasCookies
                ? `GraphQL cookies last updated ${formatTimestamp(cookieStatus.lastUpdated)}.`
                : 'GraphQL cookies not stored yet.'}
            </p>
          ) : null}
        </CardContent>
      </Card>

      {/* Manual cookie entry keeps GraphQL usable while we finish the OAuth bridge. */}
      <CookieManagerCard />
    </div>
  );
}

function useCookieManager() {
  const [cookies, setCookies] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const cookiesQueryKey = useMemo(() => getQueryKey(trpc.social.getCookiesStatus), []);
  const updateCookiesMutation = useUpdateSocialCookies();
  const deleteCookiesMutation = useDeleteSocialCookies();

  const submitCookies = useCallback(async () => {
    const normalizedCookies = toTrimmedNonEmptyString(cookies);
    if (!normalizedCookies) {
      setError('Paste your Twitter cookies first');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await updateCookiesMutation.mutateAsync({ cookies: normalizedCookies });
      await queryClient.invalidateQueries({ queryKey: cookiesQueryKey });
      toast.success('Twitter cookies stored successfully');
      setCookies('');
    } catch (error_: unknown) {
      const message = error_ instanceof Error ? error_.message : 'Failed to store cookies';
      setError(message);
      toast.error('Unable to store cookies', {
        description: message,
      });
    } finally {
      setSubmitting(false);
    }
  }, [cookies, cookiesQueryKey, queryClient, updateCookiesMutation]);

  const handleSubmitCookies = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      submitCookies().catch((unknownError: unknown) => {
        console.error('Cookie submission failed', unknownError);
      });
    },
    [submitCookies]
  );

  const clearCookies = useCallback(async () => {
    setClearing(true);
    setError(null);
    try {
      // TanStack Query v5 requires explicit undefined for void mutations
      // eslint-disable-next-line unicorn/no-useless-undefined
      await deleteCookiesMutation.mutateAsync(undefined);
      await queryClient.invalidateQueries({ queryKey: cookiesQueryKey });
      toast.success('Twitter cookies removed');
    } catch (error_: unknown) {
      const message = error_ instanceof Error ? error_.message : 'Failed to delete cookies';
      setError(message);
      toast.error('Unable to delete cookies', {
        description: message,
      });
    } finally {
      setClearing(false);
    }
  }, [cookiesQueryKey, deleteCookiesMutation, queryClient]);

  const handleClearCookies = useCallback(() => {
    clearCookies().catch((unknownError: unknown) => {
      console.error('Cookie clearing failed', unknownError);
    });
  }, [clearCookies]);

  function CookieManagerCard() {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Paste Twitter cookies</CardTitle>
          <CardDescription>
            Provide your `auth_token` and `ct0` cookies from an authenticated Twitter session. We encrypt them before
            storing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmitCookies}>
            <Textarea
              value={cookies}
              onChange={(event) => {
                setCookies(event.target.value);
              }}
              placeholder="auth_token=...; ct0=..."
              className="h-32"
            />
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" disabled={submitting} className="flex-1">
                {submitting ? (
                  <>
                    <CuteSpinner size={16} className="mr-2 border-current/40 border-t-current" />
                    Saving…
                  </>
                ) : (
                  'Save cookies'
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={clearing}
                onClick={handleClearCookies}
                className="flex-1"
              >
                {clearing ? (
                  <>
                    <CuteSpinner size={16} className="mr-2 border-current/40 border-t-current" />
                    Removing…
                  </>
                ) : (
                  'Delete cookies'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return {
    Component: CookieManagerCard,
    actions: {
      setCookies,
      submitCookies,
      clearCookies,
    },
  };
}

function formatTimestamp(value: Date | null): string {
  if (!value) {
    return 'just now';
  }
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(value);
}
const DevLoginErrorSchema = z
  .object({
    status: z.number().optional(),
    statusText: z.string().optional(),
    message: z.string().optional(),
  })
  .passthrough();

const DevLoginResponseSchema = z
  .object({
    error: z.unknown().optional(),
    data: z.unknown().optional(),
  })
  .passthrough();

const HasSessionSchema = z
  .object({
    sessionToken: z.string().optional(),
    user: z.unknown().optional(),
  })
  .passthrough();

const CookieStatusSchema = z
  .object({
    hasCookies: z.boolean().optional(),
    lastUpdated: z.union([z.string(), z.date()]).optional(),
  })
  .passthrough();
